import React from 'react'
import { mutate } from 'swr'
import { format } from 'date-fns'
import { useSnackbar } from 'notistack'
import {
  Grid,
  Stack,
  MenuItem,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  CircularProgress
} from '@mui/material'
import { ArrowForward } from '@mui/icons-material'

import CommonPopoverForm from '@components/common/PopoverForm/CommonPopoverForm'

import unformatDate from '@utils/unformatDate'
import numberWithCommas from '@utils/numberWithCommas'
import API_URL from '@config/services'
import { useTranslation } from '@contexts/translation'
import { updatePayment, updatePaymentInvoice } from '@api/payments'
import { useFetchData } from '@api/fetchData'

import useTableStyles from '@styles/table/table'
import CommonModal from '@components/common/Modal/CommonModal'
import { responseHandler } from '@utils/responseHandler'
import { ApiClientResult } from '@/types/api'

interface PaymentType {
  id: number
  label: string
  value: string
}

interface InvoicePayment {
  pk: string | number
  amount: number
  invoice: {
    code: string
    due_date: string
    amount: number
    balance: number
    [key: string]: any
  }
  [key: string]: any
}

interface Payment {
  pk: string | number
  display: string
  date: string
  type: string
  type_display: string
  amount: number
  currency_rate: number
  amount_ref: number
  currency_display: string
  invoice_payments?: InvoicePayment[]
  [key: string]: any
}

interface PopoverFormValues {
  calendarPopover?: Date
  selectPopover?: string
  textPopover?: string | number
  [key: string]: any
}

interface MatchInvoicesModalProps {
  handleClose: () => void
  paymentId: string | number
  open: boolean
  fetchPaymentsURL: string
  currency?: string
}

const MatchInvoicesModal = ({
  handleClose,
  paymentId,
  open,
  fetchPaymentsURL,
  currency
}: MatchInvoicesModalProps) => {
  const classesTable = useTableStyles()

  const { t, dictionary } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const paymentURL = paymentId
    ? API_URL.PAYMENTS.GET_PAYMENT_PK(paymentId)
    : null
  const { data: paymentData, isLoading } = useFetchData<Payment>(paymentURL)

  const PAYMENT_TYPES: PaymentType[] = [
    {
      id: 1,
      label: t('cash'),
      value: 'E'
    },
    {
      id: 2,
      label: t('check'),
      value: 'C'
    },
    {
      id: 3,
      label: t('card'),
      value: 'U'
    },
    {
      id: 4,
      label: t('lcr'),
      value: 'L'
    },
    {
      id: 5,
      label: t('transfer'),
      value: 'V'
    },
    {
      id: 6,
      label: t('deposit'),
      value: 'P'
    },
    {
      id: 7,
      label: t('none'),
      value: 'S'
    }
  ]

  const handleUpdatePaymentField = async (
    field: string,
    value: any
  ): Promise<void> => {
    const response: ApiClientResult = await updatePayment(paymentData.pk, {
      [field]: value
    })

    responseHandler({
      response,
      callback: async () => {
        await mutate(paymentURL)
        await mutate(fetchPaymentsURL)
      },
      dictionary,
      msgSuccess: t('Invoicing.payment_updated'),
      snackbar: enqueueSnackbar
    })
  }

  const handleUpdatePaymentInvoiceField = async (
    invoice_id: string | number,
    field: string,
    value: any
  ): Promise<void> => {
    const response: ApiClientResult = await updatePaymentInvoice(invoice_id, {
      [field]: value
    })

    responseHandler({
      response,
      callback: async () => {
        await mutate(paymentURL)
        await mutate(fetchPaymentsURL)
      },
      dictionary,
      msgSuccess: t('Invoicing.invoice_updated'),
      snackbar: enqueueSnackbar
    })
  }

  const handleChangePaymentDate = (values: PopoverFormValues): void => {
    if (values.calendarPopover) {
      const formatedDate = format(values.calendarPopover, 'dd-MM-yyyy')
      handleUpdatePaymentField('date', formatedDate)
    }
  }

  const getTotalAmount = (arr?: InvoicePayment[]): number => {
    return (
      arr?.reduce(
        (accumulator, currentValue) =>
          accumulator + currentValue.invoice.amount,
        0
      ) || 0
    )
  }

  const getTotalBalance = (arr?: InvoicePayment[]): number => {
    return (
      arr?.reduce(
        (accumulator, currentValue) =>
          accumulator + currentValue.invoice.balance,
        0
      ) || 0
    )
  }

  const getTotalPayment = (arr?: InvoicePayment[]): number => {
    return (
      arr?.reduce(
        (accumulator, currentValue) => accumulator + currentValue.amount,
        0
      ) || 0
    )
  }

  const getDiffNumber = (): number => {
    return (
      paymentData?.amount - getTotalPayment(paymentData?.invoice_payments) || 0
    )
  }

  return (
    <CommonModal
      open={open}
      title={paymentData?.display}
      onClose={handleClose}
      maxWidth={'md'}
    >
      {isLoading && (
        <Grid item xs={12} align="center">
          <CircularProgress color="secondary" sx={{ m: 10 }} />
        </Grid>
      )}

      {!isLoading && paymentData && (
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ whiteSpace: 'nowrap' }}>
            <Stack direction="row" spacing={1} sx={{ width: 'min-content' }}>
              <Typography
                variant="subtitle2"
                color="text.primary"
                data-testid="Typography-4d91ec3f-3156-4213-aef4-da54dda6e263"
              >
                {t('date')}
              </Typography>
              <span data-testid="span-53b72d58-f4bd-4016-b18a-0f5aa0e51677">
                <CommonPopoverForm
                  variant={'changeOption'}
                  buttonLabel={paymentData.date}
                  propertyLabel={t('date')}
                  type="calendar"
                  defaultValue={unformatDate(paymentData.date)}
                  onSubmit={handleChangePaymentDate}
                  data_testid="CommonPopoverForm-d1292ef2-726a-43c5-9614-a86ea013f0e0"
                />
              </span>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ width: 'min-content' }}>
              <Typography
                variant="subtitle2"
                color="text.primary"
                data-testid="Typography-5bfe2947-63d8-4802-b203-939eb3af4240"
              >
                {t('type')}
              </Typography>
              <span data-testid="span-1d569efa-1597-41db-ab04-b9e3549e9968">
                <CommonPopoverForm
                  variant={'changeOption'}
                  buttonLabel={paymentData.type_display}
                  propertyLabel={t('type')}
                  type="select"
                  defaultValue={paymentData.type}
                  onSubmit={values =>
                    handleUpdatePaymentField('type', values.selectPopover)
                  }
                  data_testid="CommonPopoverForm-918c833a-74bd-4a8d-981a-67691ef6c81b"
                >
                  {PAYMENT_TYPES.map(({ id, label, value }) => (
                    <MenuItem
                      value={value}
                      key={id}
                      data-testid="MenuItem-3330576f-2296-4436-ab03-f00a253bdabc"
                    >
                      {label}
                    </MenuItem>
                  ))}
                </CommonPopoverForm>
              </span>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ width: 'min-content' }}>
              <Typography
                variant="subtitle2"
                color="text.primary"
                data-testid="Typography-18b5b034-18e0-4ed8-8935-a5ab69d0fccd"
              >
                {t('payment')}
              </Typography>
              <span data-testid="span-6ab4b657-f94a-4918-a57e-a3434f0d855b">
                <CommonPopoverForm
                  variant={'changeOption'}
                  buttonLabel={numberWithCommas(paymentData.amount, 2)}
                  propertyLabel={t('payment')}
                  type="number"
                  defaultValue={paymentData.amount}
                  onSubmit={values =>
                    handleUpdatePaymentField(
                      'amount',
                      Number(values.textPopover)
                    )
                  }
                  data_testid="CommonPopoverForm-664914ff-4b7c-45ad-9635-c466e5b1954a"
                />
              </span>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ width: 'min-content' }}>
              <Typography
                variant="subtitle2"
                color="text.primary"
                data-testid="Typography-eecf94dd-0206-4e1b-9b43-29eca1c67bfa"
              >
                {t('currency_rate')}
              </Typography>
              <span data-testid="span-6bfb18c4-6832-4f4f-a3c5-9803ef447e99">
                <CommonPopoverForm
                  variant={'changeOption'}
                  buttonLabel={paymentData.currency_rate}
                  propertyLabel={t('currency_rate')}
                  type="number"
                  defaultValue={paymentData.currency_rate}
                  onSubmit={values =>
                    handleUpdatePaymentField(
                      'currency_rate',
                      Number(values.textPopover)
                    )
                  }
                  data_testid="CommonPopoverForm-eb5ef8a7-9dae-4dd9-a980-b6d5043d2021"
                />
              </span>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ width: 'min-content' }}>
              <Typography
                variant="subtitle2"
                color="text.primary"
                data-testid="Typography-980c18e0-8176-44a9-9d2b-4432e8a19f25"
              >
                {t('local_amount')}
              </Typography>
              <Typography
                variant="subtitle2"
                color="text.light"
                data-testid="Typography-5cf2f15a-618e-4a3a-ac7f-62050301de6d"
              >
                {numberWithCommas(paymentData.amount_ref, 2)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ width: 'min-content' }}>
              <Typography
                variant="subtitle2"
                color="text.primary"
                data-testid="Typography-94928e3d-6be0-4569-afa5-78d7ca39d8ca"
              >
                {t('currency')}
              </Typography>
              <Typography
                variant="subtitle2"
                color="text.light"
                data-testid="Typography-e334b839-02a9-4462-b2e9-ba2b043cc487"
              >
                {paymentData.currency_display}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  data-testid="Typography-441438aa-327e-487b-94f6-306a30ba4b71"
                >
                  {t('invoices')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TableContainer className={classesTable.container}>
                  <Table>
                    <TableHead>
                      <TableRow hover={false}>
                        <TableCell className={classesTable.headerCell}>
                          {t('invoice')}
                        </TableCell>
                        <TableCell className={classesTable.headerCell}>
                          {t('due_date')}
                        </TableCell>
                        <TableCell
                          className={classesTable.headerCell}
                          align="right"
                        >
                          {t('amount_after_tax')}
                        </TableCell>
                        <TableCell
                          className={classesTable.headerCell}
                          align="right"
                        >
                          {t('balance')}
                        </TableCell>
                        <TableCell />
                        <TableCell
                          className={classesTable.headerCell}
                          align="right"
                        >
                          {t('payment')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentData.invoice_payments?.map(invoice_payment => (
                        <TableRow
                          key={invoice_payment.pk}
                          className={
                            invoice_payment.invoice.balance === 0
                              ? classesTable.rowTotalSecondary
                              : classesTable.row
                          }
                        >
                          <TableCell>{invoice_payment.invoice.code}</TableCell>
                          <TableCell>
                            {invoice_payment.invoice.due_date}
                          </TableCell>
                          <TableCell align="right">
                            {numberWithCommas(
                              invoice_payment.invoice.amount,
                              2,
                              currency
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {numberWithCommas(
                              invoice_payment.invoice.balance,
                              2,
                              currency
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleUpdatePaymentInvoiceField(
                                  invoice_payment.pk,
                                  'amount',
                                  invoice_payment.invoice.balance
                                )
                              }
                              data-testid="IconButton-e35faa85-a4aa-464c-b643-7dd8b61ae519"
                            >
                              <ArrowForward fontSize="small" />
                            </IconButton>
                          </TableCell>
                          <TableCell align="right">
                            <CommonPopoverForm
                              buttonLabel={
                                <Typography
                                  variant="changeOptionDark"
                                  data-testid="Typography-d189dba9-6da8-4b82-8f24-efc185f4479d"
                                >
                                  {numberWithCommas(
                                    invoice_payment.amount,
                                    2,
                                    currency
                                  )}
                                </Typography>
                              }
                              propertyLabel={t('payment')}
                              type="number"
                              defaultValue={invoice_payment.amount}
                              onSubmit={values =>
                                handleUpdatePaymentInvoiceField(
                                  invoice_payment.pk,
                                  'amount',
                                  Number(values.textPopover)
                                )
                              }
                              data_testid="CommonPopoverForm-cd3fb9a2-f55a-46ec-a21d-fefe3b22a099"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {paymentData.invoice_payments?.length > 0 && (
                        <TableRow
                          hover={false}
                          className={classesTable.rowTotal}
                        >
                          <TableCell>
                            <Typography
                              variant="subtitle2"
                              color="secondary"
                              data-testid="Typography-d7e0a46d-eba7-406e-89ff-3e4e89b28651"
                            >
                              {t('total')}
                            </Typography>
                          </TableCell>
                          <TableCell />
                          <TableCell align="right">
                            <Typography
                              variant="subtitle2"
                              color="secondary"
                              data-testid="Typography-d12c4f17-5bc7-416b-ac33-d57c20dafd28"
                            >
                              {numberWithCommas(
                                getTotalAmount(paymentData.invoice_payments),
                                2,
                                currency
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="subtitle2"
                              color="secondary"
                              data-testid="Typography-e8eda84d-1363-4e58-9e35-97f6c4fbcc33"
                            >
                              {numberWithCommas(
                                getTotalBalance(paymentData.invoice_payments),
                                2,
                                currency
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell />
                          <TableCell align="right">
                            <Typography
                              variant="subtitle2"
                              color="secondary"
                              data-testid="Typography-ffbce65e-fa2a-40f7-b25d-7e3b1ba4b29a"
                            >
                              {numberWithCommas(
                                getTotalPayment(paymentData.invoice_payments),
                                2,
                                currency
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow hover={false}>
                        <TableCell colSpan={5} align="right">
                          <Typography
                            variant="subtitle2"
                            data-testid="Typography-8c3452b6-b8ee-4a56-a4bc-290db08f5731"
                          >
                            {t('remaining')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="subtitle2"
                            data-testid="Typography-2c0bba83-8657-4dd3-b7ad-9e3e50975be9"
                          >
                            {numberWithCommas(getDiffNumber(), 2, currency)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </CommonModal>
  )
}

export default MatchInvoicesModal
